import {get} from "lodash";

import {TFilterCountry} from "api/filterOptionsApi/CountryApi/types";
import {TLocationPosition} from "containers/Filters/PeopleFilters/LocationFilter/types";
import {TAppDispatch, TRootState} from "types";
import type {TCountry, TGetLocationExpandableDataResult, TLocation, TRegion} from "./types";

export const getLocationExpandableData = (
	filteredData?: (TLocation | TCountry | TRegion)[] | null,
) =>
	filteredData?.reduce<TGetLocationExpandableDataResult>((acc, curVal) => {
		const curValRegions = get(curVal, "regions");
		const curValCountries = get(curVal, "countries");

		if (curValCountries || curValRegions) {
			acc[curVal.id] = true;

			Object.assign(acc, getLocationExpandableData(curValCountries || curValRegions));
		}

		return acc;
	}, {});

/**
 * Helper to get formatted location labels (tags) based on selected IDs at any level.
 */
export const getSelectedLocations = (
	countryIDs: number[] = [],
	regionIDs: (string | number)[] = [],
	stateIDs: (string | number)[] = [],
	cityIDs: (string | number)[] = [],
	options: TCountry[] = [],
): TFilterCountry[] => {
	const selected: TFilterCountry[] = [];

	const countrySet = new Set(countryIDs);
	const regionSet = new Set(regionIDs);
	const stateSet = new Set(stateIDs);
	const citySet = new Set(cityIDs);

	options.forEach((o) => {
		const isCountryMatch = countrySet.has(o.id);

		if (isCountryMatch) {
			selected.push({
				id: o.id,
				name: o.name,
			});

			return;
		}

		// Check regions
		o.regions?.forEach((region) => {
			if (regionSet.has(region.id)) {
				selected.push({
					id: o.id,
					name: `${o.name}: ${region.name}`,
					region_id: region.id,
				});

				return;
			}

			// Check states within region
			region.states?.forEach((state) => {
				if (stateSet.has(state.id)) {
					selected.push({
						id: o.id,
						name: `${o.name}: ${region.name}: ${state.name}`,
						region_id: region.id,
						state_id: state.id,
					});

					return;
				}

				// Check cities within state
				state.cities?.forEach((city) => {
					if (citySet.has(city.id)) {
						selected.push({
							id: o.id,
							name: `${o.name}: ${region.name}: ${state.name}: ${city.name}`,
							region_id: region.id,
							state_id: state.id,
							city_id: city.id,
						});
					}
				});
			});
		});

		// Check states directly under country
		o.states?.forEach((state) => {
			if (stateSet.has(state.id)) {
				selected.push({
					id: o.id,
					name: `${o.name}: ${state.name}`,
					state_id: state.id,
				});

				return;
			}

			// Check cities under state
			state.cities?.forEach((city) => {
				if (citySet.has(city.id)) {
					selected.push({
						id: o.id,
						name: `${o.name}: ${state.name}: ${city.name}`,
						state_id: state.id,
						city_id: city.id,
					});
				}
			});
		});

		// Check cities directly under country
		o.cities?.forEach((city) => {
			if (citySet.has(city.id)) {
				selected.push({
					id: o.id,
					name: `${o.name}: ${city.name}`,
					city_id: city.id,
				});
			}
		});
	});

	return selected;
};

export const getSelectedCountriesAndRegions = (
	countryIDs: number[],
	regionIDs: number[],
	options: TCountry[],
): TFilterCountry[] => {
	return getSelectedLocations(countryIDs, regionIDs, [], [], options);
};

export const handleMoveLocation =
	(
		{
			from,
			to,
			country_id,
			region_id,
			state_id,
			city_id,
		}: {
			from: TLocationPosition;
			to: TLocationPosition;
			country_id: number;
			region_id?: string | number;
			state_id?: string | number;
			city_id?: string | number;
		},
		getFilterKeys: (
			position: TLocationPosition,
			state: TRootState,
		) => {locationKey: string; regionKey: string; stateKey?: string; cityKey?: string},
		getCandidateIds: (
			position: TLocationPosition,
			state: TRootState,
		) => {locations: number[]; regions: number[]; states?: number[]; cities?: number[]},
		setFilterAction: (filterChanges: Record<string, number[]>) => void,
	) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		const fromKeys = getFilterKeys(from, state);
		const toKeys = getFilterKeys(to, state);
		const fromData = getCandidateIds(from, state);
		const toData = getCandidateIds(to, state);

		const moveIdsBetweenFilters = (
			fromIds: number[],
			toIds: number[],
			filterCondition: (id: number) => boolean,
		) => ({
			from: fromIds.filter((id) => !filterCondition(id)),
			to: [...toIds.filter((id) => !filterCondition(id)), ...fromIds.filter(filterCondition)],
		});

		const filterChanges: Record<string, number[]> = {};

		if (city_id && fromKeys.cityKey && toKeys.cityKey && fromData.cities && toData.cities) {
			const cityMove = moveIdsBetweenFilters(
				fromData.cities,
				toData.cities,
				(id) => String(city_id) === String(id),
			);
			filterChanges[fromKeys.cityKey] = cityMove.from;
			filterChanges[toKeys.cityKey] = cityMove.to;
		} else if (
			state_id &&
			fromKeys.stateKey &&
			toKeys.stateKey &&
			fromData.states &&
			toData.states
		) {
			const stateMove = moveIdsBetweenFilters(
				fromData.states,
				toData.states,
				(id) => String(state_id) === String(id),
			);
			filterChanges[fromKeys.stateKey] = stateMove.from;
			filterChanges[toKeys.stateKey] = stateMove.to;
		} else if (region_id) {
			const regionMove = moveIdsBetweenFilters(
				fromData.regions,
				toData.regions,
				(id) => String(region_id) === String(id),
			);
			filterChanges[fromKeys.regionKey] = regionMove.from;
			filterChanges[toKeys.regionKey] = regionMove.to;
		} else {
			const countryMove = moveIdsBetweenFilters(
				fromData.locations,
				toData.locations,
				(id) => country_id === id,
			);
			filterChanges[fromKeys.locationKey] = countryMove.from;
			filterChanges[toKeys.locationKey] = countryMove.to;
		}

		dispatch(setFilterAction(filterChanges));
	};
